/**
 * Risiko-Indikatoren je Dimension.
 *
 * Maschinenlesbare Codes (siehe `RiskIndicatorCodeSchema`) plus deutsche
 * Klartext-Begründung. Die Codes werden im Stellungnahme-Generator zu
 * Textbausteinen — daher additiv erweitern, nie umbenennen.
 *
 * Schwellen orientiert an Konzept §4.2 (Risikoindikatoren je Dimension).
 */
import type {
  ActivityData,
  HealthData,
  IdentityData,
  PrivateData,
  StyleData,
} from '../schema/dimensionData.js';
import type { FinancialDataset } from '../schema/financialDataset.js';
import type { RiskIndicator } from '../schema/score.js';
import { avgCents } from './internal/money.js';

const KLUMPEN_THRESHOLD_PERCENT = 50;
const NEGATIVE_LIQUIDITY_BUFFER_CENTS = 0;
const PROFIT_LOW_THRESHOLD_CENTS = 70_000; // 700€
const HOURS_LOW_THRESHOLD = 60;
const BG_GAP_LARGE_CENTS = 80_000; // 800€

export function getIdentityRiskIndicators(data: IdentityData): RiskIndicator[] {
  const out: RiskIndicator[] = [];
  const hasIndustryRelevant = data.qualifications.some((q) => q.industryRelevant);
  if (!hasIndustryRelevant) {
    out.push({
      dimension: 'identity',
      code: 'identity_industry_outsider',
      severity: 'warning',
      message:
        'Keine branchenrelevante Qualifikation dokumentiert. Eignung muss anderweitig nachgewiesen werden.',
    });
  }
  if (!data.hasEntrepreneurialExperience) {
    out.push({
      dimension: 'identity',
      code: 'identity_no_sales_experience',
      severity: 'info',
      message:
        'Keine vorherige unternehmerische Erfahrung — kaufmännische und vertriebliche Kompetenzen prüfen.',
    });
  }
  return out;
}

export function getActivityRiskIndicators(data: ActivityData): RiskIndicator[] {
  const out: RiskIndicator[] = [];
  const max = data.topClientsByRevenue.reduce((m, c) => Math.max(m, c.revenueSharePercent), 0);
  if (max > KLUMPEN_THRESHOLD_PERCENT) {
    out.push({
      dimension: 'activity',
      code: 'activity_klumpenrisiko',
      severity: 'critical',
      message: `Klumpenrisiko: ein Auftraggeber stellt ${Math.round(max)}% des Umsatzes (Schwelle 50%).`,
    });
  }
  if (!data.hasRecurringRevenue) {
    out.push({
      dimension: 'activity',
      code: 'activity_no_recurring_revenue',
      severity: 'warning',
      message:
        'Keine wiederkehrenden Umsätze — Tragfähigkeit hängt an kontinuierlicher Neuakquise.',
    });
  }
  if (!data.hasDocumentedSalesProcess) {
    out.push({
      dimension: 'activity',
      code: 'activity_no_sales_process',
      severity: 'info',
      message: 'Kein systematischer Vertriebsprozess dokumentiert.',
    });
  }
  return out;
}

export function getStyleRiskIndicators(data: StyleData): RiskIndicator[] {
  const out: RiskIndicator[] = [];
  if (!data.targetGroupDefined) {
    out.push({
      dimension: 'style',
      code: 'style_undefined_target_group',
      severity: 'warning',
      message: 'Zielgruppe nicht klar umrissen — Akquise und Pricing erschwert.',
    });
  }
  if (
    typeof data.selfReportedHourlyRateEuro === 'number' &&
    typeof data.industryFloorHourlyRateEuro === 'number' &&
    data.industryFloorHourlyRateEuro > 0 &&
    data.selfReportedHourlyRateEuro < 0.8 * data.industryFloorHourlyRateEuro
  ) {
    out.push({
      dimension: 'style',
      code: 'style_hourly_rate_below_floor',
      severity: 'critical',
      message: `Stundensatz ${data.selfReportedHourlyRateEuro}€ liegt unter 80% der Branchenuntergrenze (${data.industryFloorHourlyRateEuro}€).`,
    });
  }
  if (!data.pricingStrategyDocumented) {
    out.push({
      dimension: 'style',
      code: 'style_no_pricing_strategy',
      severity: 'info',
      message: 'Keine Pricing-Strategie dokumentiert.',
    });
  }
  return out;
}

export function getResourcesRiskIndicators(
  financialDataset: FinancialDataset | undefined,
): RiskIndicator[] {
  const out: RiskIndicator[] = [];
  if (!financialDataset) return out;

  const months = financialDataset.monthlyAggregates;
  const avgRevenueCents = avgCents(months.map((m) => m.revenueCents));
  const avgCostsCents = avgCents(months.map((m) => m.costsCents));
  // avgCents liefert bereits gerundete Integer-Cents — die Differenz bleibt
  // damit Integer-konform und schützt vor Float-Drift bei MoneyCents-
  // Aggregationen (siehe internal/money.ts).
  const avgProfitCents = (avgRevenueCents as number) - (avgCostsCents as number);

  const liq = financialDataset.liquidity;
  if (liq) {
    const net = (liq.liquidAssetsCents as number) - (liq.shortTermLiabilitiesCents as number);
    if (net < NEGATIVE_LIQUIDITY_BUFFER_CENTS) {
      out.push({
        dimension: 'resources',
        code: 'resources_negative_liquidity',
        severity: 'critical',
        message:
          'Negative Liquiditätsreichweite: kurzfristige Verbindlichkeiten übersteigen liquide Mittel.',
      });
    }
  }

  if (avgProfitCents < PROFIT_LOW_THRESHOLD_CENTS) {
    out.push({
      dimension: 'resources',
      code: 'resources_profit_below_threshold',
      severity: 'warning',
      message: `Durchschnittlicher Monatsgewinn ${Math.round(avgProfitCents / 100)}€ unter 700€-Schwelle (Konzept §4.2 Risikoindikator).`,
    });
  }

  const hours = financialDataset.hourlyRate.billableHoursPerMonth as number | undefined;
  if (typeof hours === 'number' && hours < HOURS_LOW_THRESHOLD) {
    out.push({
      dimension: 'resources',
      code: 'resources_low_billable_hours',
      severity: 'info',
      message: `Nur ${hours} abrechenbare Stunden/Monat — Auslastung deutlich unter Vollzeit.`,
    });
  }

  if (typeof hours === 'number' && hours > 0 && avgProfitCents <= 0) {
    out.push({
      dimension: 'resources',
      code: 'resources_negative_hourly_rate',
      severity: 'critical',
      message:
        'Bereinigter Stundensatz negativ oder null — Tätigkeit deckt nicht ihre eigenen Kosten.',
    });
  }

  return out;
}

export function getHealthRiskIndicators(data: HealthData): RiskIndicator[] {
  const out: RiskIndicator[] = [];
  if (data.conditionsAffectWork) {
    out.push({
      dimension: 'health',
      code: 'health_chronic_condition_affects_work',
      severity: 'warning',
      message:
        'Gesundheitliche Einschränkung beeinträchtigt nach Eigenangabe die Tätigkeit. Ärztliche Bescheinigung empfohlen.',
    });
  }
  const hours = (data.hoursWorkedPerWeek as number | undefined) ?? 0;
  if (hours > 55) {
    out.push({
      dimension: 'health',
      code: 'health_overwork',
      severity: 'warning',
      message: `Selbstgemeldete Wochenarbeitszeit ${hours}h überschreitet nachhaltige Grenze (55h).`,
    });
  }
  const r = data.selfReportedResilienceScore ?? 0;
  if (r > 0 && r <= 3) {
    out.push({
      dimension: 'health',
      code: 'health_low_resilience',
      severity: 'warning',
      message: 'Selbsteingeschätzte Belastbarkeit niedrig (≤3 von 10).',
    });
  }
  return out;
}

export function getPrivateRiskIndicators(data: PrivateData, bgGapCents?: number): RiskIndicator[] {
  const out: RiskIndicator[] = [];
  if (typeof bgGapCents === 'number' && bgGapCents > BG_GAP_LARGE_CENTS) {
    out.push({
      dimension: 'private',
      code: 'private_bg_gap_large',
      severity: 'critical',
      message: `BG-Tragfähigkeitslücke ${Math.round(bgGapCents / 100)}€/Monat — Selbständigkeit deckt Bedarf nicht.`,
    });
  }
  // 'not_applicable' (BG ohne Kinder) ist kein Risiko, nur 'not_secured'.
  if (data.childcareStatus === 'not_secured') {
    out.push({
      dimension: 'private',
      code: 'private_no_childcare',
      severity: 'warning',
      message:
        'Kinderbetreuung nicht gesichert — Zumutbarkeit Vermittlung in abhängige Beschäftigung prüfen.',
    });
  }
  if (data.hasCareResponsibility) {
    out.push({
      dimension: 'private',
      code: 'private_high_care_burden',
      severity: 'info',
      message: 'Pflegeverantwortung im Haushalt — Zumutbarkeitsgrund nach § 10 SGB II prüfen.',
    });
  }
  return out;
}
